/**
 * Offline-order SOURCES (handoff §11, WI #3311) — where the rooms of an offline order come from.
 * Field names mirror the BE records in `be-hotel-extranet-service` one to one: the source tail of
 * `dto/response/BookingRow` (`sourceType … sourceEffect`), `dto/response/SourceEffect`,
 * `dto/request/SupplierSourcedOrderRequest`, and the `sourceType` filter of `dto/request/OrderFilter`.
 *
 * Two paths, one question first ("where do the rooms come from?"):
 *  - CONTRACT — sold out of inventory the agency already holds: rooms come off that contract's
 *    availability, the cost is the contracted rate (computed server-side, never typed) and NO
 *    supplier purchase opens (its cost is already on the books through the instalments);
 *  - SUPPLIER — bought in for this one stay, outside any contract: availability is untouched,
 *    the cost is typed and exactly one purchase (`PO-…`) opens on the supplier's payable.
 * A `null` source is a direct / legacy order (hotel managers, pre-F3 rows) — "Direct" on screen.
 */

export type OrderSourceType = 'CONTRACT' | 'SUPPLIER';

/** The list filter vocabulary: `DIRECT` is the query-string name for "no source" (a filter
 *  cannot send null — `OrderFilter.SOURCE_DIRECT` on the BE). */
export type OrderSourceFilter = 'CONTRACT' | 'SUPPLIER' | 'DIRECT';

export const ORDER_SOURCE_FILTERS: readonly OrderSourceFilter[] = ['CONTRACT', 'SUPPLIER', 'DIRECT'];

export function isOrderSourceFilter(v: unknown): v is OrderSourceFilter {
  return (ORDER_SOURCE_FILTERS as readonly unknown[]).includes(v);
}

export type SourceEffectKind = 'ROOMS_OFF_CONTRACT' | 'PURCHASE_OPENED';

/** `SourceEffect` — what registering a sourced order DID besides the order itself (AC-11.9: the
 *  confirmation toast states which happened). Carried on the two create responses only
 *  (`BookingRow.sourceEffect`), null on every read. */
export interface SourceEffect {
  kind: SourceEffectKind;
  /** Rooms the order holds. */
  rooms: number;
  /** ROOMS_OFF_CONTRACT: the contract the rooms came off. */
  contractReference: string | null;
  /** PURCHASE_OPENED: the purchase opened on the supplier. */
  purchaseReference: string | null;
  /** PURCHASE_OPENED: the purchase amount, in `currency`. */
  amount: number | null;
  currency: string | null;
}

/** The source tail every `BookingRow` (and `ClientOrderRow`) carries after F3 — header columns
 *  plus the labels the BE resolves (`contractReference`, `supplierName`) and the money it derives
 *  (`margin` only when the sale and cost currencies match, never converted — R3). */
export interface OrderSourceFields {
  /** CONTRACT | SUPPLIER; null = direct / legacy. */
  sourceType: OrderSourceType | null;
  /** The contract the rooms came off (CONTRACT only). */
  contractId: string | null;
  contractReference: string | null;
  /** The counterparty the rooms cost us: the contract's supplier, or the one bought from. */
  supplierId: string | null;
  supplierName: string | null;
  /** What the rooms cost us, in `costCurrency`. */
  cost: number | null;
  /** The contract's or the supplier's currency — never the sale's by construction. */
  costCurrency: string | null;
  /** totalPrice − cost, only when `currency === costCurrency`; else null. */
  margin: number | null;
  /** margin ÷ totalPrice × 100, rounded to the integer the screens print ("23%"). */
  marginPct: number | null;
  /** The counterparty's own confirmation number. */
  supplierRef: string | null;
  /** The hotel: the header snapshot for a header-only order, else the property name. */
  hotelName: string | null;
  /** The room type: the header snapshot for a header-only order, else the resolved name. */
  roomTypeName: string | null;
  /** Set on the create responses only. */
  sourceEffect: SourceEffect | null;
}

/** `POST /extranet/orders/supplier-sourced` (AC-11.7/11.8) — a header-only order (no consumption
 *  rows, no inventory movement) plus exactly ONE supplier purchase carrying the sale's id and
 *  sell price. `cost` is what the supplier charges us (in `costCurrency`, default the supplier's);
 *  `totalPrice` is what we sell for (in `currency`, default the group's). 422 codes: COST_INVALID,
 *  INVALID_DATE_RANGE, SUPPLIER_ARCHIVED, OPTION_DATE_INVALID; 404 SUPPLIER_NOT_FOUND / CLIENT_NOT_FOUND. */
export interface SupplierSourcedOrderRequest {
  supplierId: string;
  /** The hotel the guest sleeps in — the supplier's own name when it is the hotel, free text when
   *  an agency resells one. */
  hotelName: string;
  /** Room type as contracted with the supplier (free text). */
  roomTypeName?: string;
  guestName: string;
  guestEmail?: string;
  guestPhone?: string;
  checkIn: string;
  checkOut: string;
  rooms: number;
  totalPrice: number;
  currency?: string;
  cost: number;
  costCurrency?: string;
  supplierRef?: string;
  optionDate?: string;
  clientId?: string;
  externalRef?: string;
  internalNote?: string;
}
